// src/api/axios/interceptors.ts
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { toast } from 'react-toastify'

import ROUTER from '@/routes/ROUTER'
import axios, { type AxiosRequestConfig } from 'axios'
import { ENV_CONFIG } from '../../utils/env'
import {
  STORAGE,
  deleteAuthToken,
  deleteLocalStorage,
  deleteSessionStorage,
  getAuthToken,
  getLocalStorage,
  getRefreshToken,
  setAuthToken,
  setRefreshToken, // Add import
} from '../../utils/storage'
import identityService from '../IdentityService'

// ============ TOKEN REFRESH MANAGEMENT ============
// Tránh multiple refresh calls đồng thời
let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (error: unknown) => void
}> = []

const processQueue = (error: unknown, token: string | null = null) => {
  for (const prom of failedQueue) {
    if (error) {
      prom.reject(error)
    } else if (token) {
      prom.resolve(token)
    }
  }

  failedQueue = []
}

// Kiểm tra remember me để lưu token đúng nơi
const isRememberMe = (): boolean => {
  return getLocalStorage<string>(STORAGE.TOKEN) !== null
}

// Lưu tokens mới sau khi refresh
const saveTokens = (accessToken: string, refreshToken?: string): void => {
  const remember = isRememberMe()
  setAuthToken(accessToken, remember)
  if (refreshToken) {
    setRefreshToken(refreshToken, remember)
  }
}

// Xóa tất cả token khi logout
const clearAllTokens = (): void => {
  deleteAuthToken()
  deleteLocalStorage(STORAGE.REFRESH_TOKEN)
  deleteSessionStorage(STORAGE.REFRESH_TOKEN)
}

const isUnauthorized = (error: unknown): boolean => {
  const e = error as { errorCode?: unknown; response?: { status?: number } } | null
  return e?.errorCode === 401 || e?.response?.status === 401
}

// Refresh token action - gọi API refresh; expired = refresh bị từ chối (401), không phải lỗi mạng
const refreshTokenAction = async (): Promise<{
  success: boolean
  expired?: boolean
  accessToken?: string
}> => {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    return { success: false, expired: true }
  }

  try {
    // _retry: refresh không được vào nhánh 401 của interceptor, nếu không sẽ tự chờ chính nó
    const response = await identityService.refreshToken(refreshToken, {
      _retry: true,
    } as AxiosRequestConfig)

    if (!response.isError && response.object?.accessToken) {
      saveTokens(response.object.accessToken, response.object.refreshToken)
      return { success: true, accessToken: response.object.accessToken }
    }
    return { success: false, expired: true }
  } catch (error) {
    return { success: false, expired: isUnauthorized(error) }
  }
}

// ============ AXIOS INTERCEPTORS ============
export const setupInterceptors = (instance: AxiosInstance) => {
  // Request Interceptor - Gắn token vào header
  instance.interceptors.request.use(
    (config) => {
      if (config.data instanceof FormData) {
        config.headers['Content-Type'] = undefined
      }
      const token = getAuthToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      // Chống double-click/network-retry: BE đọc header này để replay response cũ
      // thay vì chạy lại business logic (xem QLRV.Application.Infrastructure.Locking.IdempotencyLock)
      if (config.method?.toLowerCase() === 'post' && !config.headers['Idempotency-Key']) {
        config.headers['Idempotency-Key'] =
          crypto.randomUUID?.() ??
          ([1e7].toString() + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
            (
              Number(c) ^
              (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (Number(c) / 4)))
            ).toString(16)
          )
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  // Response Interceptor - Xử lý response và refresh token
  instance.interceptors.response.use(
    async (response) => {
      // Blob response trả về nguyên vẹn
      if (response.config.responseType === 'blob') {
        return response
      }

      const { isError, object, statusCode, errorMessage } = response.data
      const originalRequest = response.config as InternalAxiosRequestConfig & {
        _retry?: boolean
      }

      // Xử lý HTTP 403 Forbidden
      // if (statusCode === 403) {
      //   toast.error('Bạn không có quyền thực hiện thao tác này.')
      //   return Promise.reject({
      //     errorCode: 403,
      //     errorMessage: 'Forbidden',
      //     config: response.config,
      //   })
      // }

      // Xử lý HTTP 401 Unauthorized
      if (statusCode === 401 && !originalRequest._retry) {
        // Nếu đang refresh rồi thì queue request lại
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`
              return instance(originalRequest)
            })
            .catch((err) => Promise.reject(err))
        }

        originalRequest._retry = true
        isRefreshing = true

        try {
          const refreshResult = await refreshTokenAction()

          if (refreshResult.success && refreshResult.accessToken) {
            // Refresh thành công - cập nhật token và retry tất cả queued requests
            processQueue(null, refreshResult.accessToken)
            originalRequest.headers.Authorization = `Bearer ${refreshResult.accessToken}`
            return instance(originalRequest)
          }

          // Refresh thất bại
          processQueue(new Error('Refresh token failed'), null)
          if (refreshResult.expired) {
            clearAllTokens()
            toast.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.')
            window.location.href = ROUTER.HOME
          }
          return Promise.reject('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.')
        } catch (refreshError) {
          processQueue(refreshError, null)
          clearAllTokens()
          window.location.href = ROUTER.HOME
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      }
      // Xử lý lỗi từ API (không phải HTTP error)
      if (isError && statusCode !== '200') {
        const objectMessage =
          object &&
          typeof object === 'object' &&
          'items' in object &&
          Array.isArray(object.items) &&
          'message' in object
            ? object.message
            : object
        const message =
          errorMessage ||
          (typeof objectMessage === 'string' ? objectMessage : 'Thao tác không thành công')
        if (statusCode !== 401) {
          toast.error(message)
        }
        return Promise.reject({
          errorCode: statusCode,
          errorMessage: message,
          object,
          config: response.config,
        })
      }
      return response.data
    },

    async (error) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean
      }

      // Xử lý HTTP 403 Forbidden
      if (error.response?.status === 403) {
        toast.error('Bạn không có quyền thực hiện thao tác này.')
        return Promise.reject(error)
      }

      // Xử lý HTTP 401 Unauthorized
      if (error.response?.status === 401 && !originalRequest._retry) {
        // Nếu đang refresh rồi thì queue request lại
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`
              return instance(originalRequest)
            })
            .catch((err) => Promise.reject(err))
        }

        originalRequest._retry = true
        isRefreshing = true

        try {
          const refreshResult = await refreshTokenAction()

          if (refreshResult.success && refreshResult.accessToken) {
            // Refresh thành công - cập nhật token và retry tất cả queued requests
            processQueue(null, refreshResult.accessToken)
            originalRequest.headers.Authorization = `Bearer ${refreshResult.accessToken}`
            return instance(originalRequest)
          }

          // Refresh thất bại
          processQueue(new Error('Refresh token failed'), null)
          if (refreshResult.expired) {
            clearAllTokens()
            toast.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.')
            window.location.href = ROUTER.HOME
          }
          return Promise.reject(error)
        } catch (refreshError) {
          processQueue(refreshError, null)
          clearAllTokens()
          window.location.href = ROUTER.HOME
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      }

      // Các lỗi HTTP khác
      const data = error.response?.data
      const errorMessage =
        data?.errorMessage ||
        data?.message ||
        (typeof data?.object === 'string' ? data.object : '') ||
        error.message ||
        'Lỗi không xác định từ server'
      toast.error(errorMessage)
      return Promise.reject(error)
    }
  )
}

// ============ AXIOS INSTANCE ============
const instance = axios.create({
  baseURL: ENV_CONFIG.VITE_BACKEND_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
})

setupInterceptors(instance)

export default instance
